import React, { useState, useEffect } from 'react';
import { Calendar, Sparkles, Moon, ArrowRight } from 'lucide-react';
import { events } from '../data/mock';

const Events = () => {
  const heroImages = [
    'https://images.pexels.com/photos/35980734/pexels-photo-35980734.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    'https://images.unsplash.com/photo-1769017714391-98475018174c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxzZXJlbmUlMjBCdWRkaGElMjBzdGF0dWUlMjB0ZW1wbGUlMjBjZXJlbW9ueSUyMGdvbGRlbiUyMHBlYWNlZnVsJTIwbWVkaXRhdGlvbnxlbnwwfHx8fDE3NzcwNjk3NDZ8MA&ixlib=rb-4.1.0&q=85',
    'https://images.unsplash.com/photo-1618604250212-01fb07f80ea4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwzfHxzZXJlbmUlMjBCdWRkaGElMjBzdGF0dWUlMjB0ZW1wbGUlMjBjZXJlbW9ueSUyMGdvbGRlbiUyMHBlYWNlZnVsJTIwbWVkaXRhdGlvbnxlbnwwfHx8fDE3NzcwNjk3NDZ8MA&ixlib=rb-4.1.0&q=85',
  ];

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const imagePromises = heroImages.map(
      (src) =>
        new Promise((resolve, reject) => {
          const img = new Image();
          img.src = src;
          img.onload = resolve;
          img.onerror = reject;
        })
    );
    Promise.all(imagePromises).then(() => setIsLoaded(true));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!isLoaded) return;
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 60000);
    return () => clearInterval(interval);
  }, [heroImages.length, isLoaded]);

  const poyaSchedule = [
    { month: 'January', date: 'Jan 13' },
    { month: 'February', date: 'Feb 12' },
    { month: 'March', date: 'Mar 14' },
    { month: 'April', date: 'Apr 13' },
    { month: 'May', date: 'May 2' },
    { month: 'June · Poson', date: 'Jun 11', featured: true },
  ];

  return (
    <div className="min-h-screen bg-[#ede1c5] text-stone-900">
      {/* HERO with slideshow */}
      <section className="relative h-[680px] flex items-end overflow-hidden bg-black pt-20">
        {heroImages.map((image, index) => (
          <div
            key={index}
            className="absolute inset-0 transition-opacity duration-[2000ms] ease-in-out"
            style={{
              backgroundImage: `url(${image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: currentImageIndex === index ? 1 : 0,
              zIndex: currentImageIndex === index ? 1 : 0,
            }}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-[#ede1c5]/65 via-transparent to-[#ede1c5] z-10" />

        <div className="relative z-20 max-w-6xl mx-auto px-6 lg:px-10 w-full pb-20">
          <div className="flex items-center gap-3 text-amber-700/90 tracking-[0.3em] text-[11px] uppercase mb-6">
            <span className="h-px w-12 bg-amber-600/60" />
            <Sparkles size={14} />
            <span>Ceremonies · Gatherings · Poya</span>
          </div>
          <h1
            className="font-serif-display text-5xl md:text-7xl leading-[1.05] text-stone-900 max-w-3xl"
            style={{ textShadow: '0 4px 30px rgba(0,0,0,0.6)' }}
          >
            Events &<br />
            <span className="italic text-amber-800">celebrations.</span>
          </h1>
          <p
            className="mt-6 max-w-xl text-lg text-stone-800 leading-relaxed"
            style={{ textShadow: '0 2px 10px rgba(0,0,0,0.6)' }}
          >
            Join our community for sacred ceremonies, Poya day observances, and recurring practice.
          </p>

          <div className="flex gap-2 mt-10">
            {heroImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`transition-all duration-300 h-1.5 rounded-full ${
                  index === currentImageIndex ? 'bg-amber-600 w-10' : 'bg-white/30 w-5 hover:bg-[#fbf5e3]/60'
                }`}
                aria-label={`Slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* SPECIAL FEATURED EVENT */}
      {events.filter((e) => e.isSpecial).map((event) => (
        <section
          key={event.id}
          className="py-24 border-t border-amber-500/40"
          data-testid={`special-event-${event.id}`}
        >
          <div className="max-w-5xl mx-auto px-6 lg:px-10">
            <div
              className="relative rounded-3xl overflow-hidden border border-amber-500/40 p-10 md:p-14"
              style={{
                background:
                  'radial-gradient(circle at 15% 10%, rgba(220,170,80,0.32) 0%, transparent 55%), linear-gradient(135deg, #fbf5e3 0%, #f1ddae 100%)',
                boxShadow:
                  '0 1px 0 rgba(255,255,255,0.7) inset, 0 30px 60px -30px rgba(120,80,20,0.3)',
              }}
            >
              <div className="absolute -bottom-32 -right-20 w-[420px] h-[420px] rounded-full bg-amber-400/25 blur-3xl" />

              <div className="relative">
                <div className="flex items-center gap-3 text-amber-800 tracking-[0.3em] text-[11px] uppercase mb-5">
                  <Sparkles size={13} />
                  <span>Featured · Upcoming</span>
                  <span className="h-px w-10 bg-amber-700/60" />
                </div>

                <h2 className="font-serif-display text-4xl md:text-5xl text-stone-900 leading-[1.1] mb-4">
                  {event.title}
                </h2>

                <div className="flex flex-wrap items-center gap-5 mb-8 pb-8 border-b border-amber-700/20">
                  <div className="inline-flex items-center gap-2 text-amber-800 text-sm tracking-wide">
                    <Calendar size={16} />
                    <span className="font-medium">{event.date}</span>
                  </div>
                  <div className="inline-flex items-center gap-2 text-amber-800 text-sm tracking-wide">
                    <Moon size={14} />
                    <span>Poson Full Moon Poya</span>
                  </div>
                  <div className="text-stone-600 text-sm tracking-wide">
                    Alabama Buddhist Vihāra · Pell City, AL
                  </div>
                </div>

                <div className="space-y-5 text-stone-700 leading-relaxed text-[15px] md:text-base max-w-3xl">
                  {(event.details || []).map((para, idx) => (
                    <p key={idx} className={idx === 0 ? 'text-stone-800' : ''}>
                      {para}
                    </p>
                  ))}
                </div>

                <div className="mt-10 flex flex-col sm:flex-row gap-4">
                  <a
                    href="/contact"
                    className="inline-flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-7 py-3.5 rounded-full text-sm tracking-wide shadow-md shadow-amber-700/20"
                  >
                    Express Interest <ArrowRight size={15} />
                  </a>
                  <a
                    href="/donate"
                    className="inline-flex items-center justify-center gap-2 border border-amber-700/40 hover:border-amber-700 text-stone-800 hover:text-amber-800 hover:bg-amber-50 px-7 py-3.5 rounded-full text-sm tracking-wide"
                  >
                    Support the Program
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* REGULAR EVENTS */}
      <section className="py-24 border-t border-amber-500/40">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <div className="text-amber-700/80 text-[11px] tracking-[0.3em] uppercase mb-3">
              Weekly Rhythm
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-stone-900">
              Regular gatherings
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {events
              .filter((e) => !e.featured)
              .map((event) => (
                <div
                  key={event.id}
                  className="p-8 rounded-2xl border border-amber-500/40 bg-[#fbf5e3]/80 hover:bg-[#fbf5e3]/95 hover:border-amber-500/40 transition-all"
                >
                  <h3 className="font-serif-display text-2xl text-stone-900 mb-4 leading-tight">
                    {event.title}
                  </h3>
                  {event.date && (
                    <div className="flex items-center gap-2 text-amber-800 text-sm tracking-wide mb-4">
                      <Calendar size={15} />
                      <span>{event.date}</span>
                    </div>
                  )}
                  <p className="text-stone-600 leading-relaxed">{event.description}</p>
                </div>
              ))}
          </div>
        </div>
      </section>

      {/* POYA */}
      <section className="py-24 bg-[#e3d4af] border-t border-amber-500/40">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <Moon className="mx-auto text-amber-700/80 mb-4" size={28} />
            <div className="text-amber-700/80 text-[11px] tracking-[0.3em] uppercase mb-3">
              Sacred Full Moons
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-stone-900">Poya days</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto mb-12">
            <div className="p-8 rounded-2xl border border-amber-500/40 bg-[#fbf5e3]/70">
              <h3 className="font-serif-display text-xl text-stone-900 mb-3">What are Poya days?</h3>
              <p className="text-stone-600 leading-relaxed">
                Poya days mark the phases of the moon and hold special significance in Buddhist
                tradition — days of heightened practice, reflection, and merit-making.
              </p>
            </div>
            <div className="p-8 rounded-2xl border border-amber-500/40 bg-[#fbf5e3]/70">
              <h3 className="font-serif-display text-xl text-stone-900 mb-3">Poya activities</h3>
              <ul className="text-stone-600 space-y-2">
                <li>· Extended meditation sessions</li>
                <li>· Dharma discussions</li>
                <li>· Observing the Eight Precepts</li>
                <li>· Community chanting</li>
                <li>· Dāna offerings</li>
              </ul>
            </div>
          </div>

          <div className="max-w-5xl mx-auto p-10 rounded-2xl border border-amber-500/40 bg-gradient-to-br from-[#fbf5e3]/95 to-[#f3e7c4]/90">
            <h3 className="font-serif-display text-2xl text-stone-900 mb-6">
              2026 Full Moon Poya Schedule
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {poyaSchedule.map((p) => (
                <div
                  key={p.month}
                  className={`p-4 rounded-xl border ${
                    p.featured
                      ? 'border-amber-600/60 bg-amber-100/50'
                      : 'border-amber-500/40 bg-[#fbf5e3]/80'
                  }`}
                >
                  <div className="font-serif-display text-stone-900">{p.month}</div>
                  <div
                    className={`text-sm tracking-wide ${
                      p.featured ? 'text-amber-800' : 'text-stone-600'
                    }`}
                  >
                    Full Moon · {p.date}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-stone-500 mt-5 tracking-wide">
              * Additional months available upon request
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 border-t border-amber-500/40">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-serif-display text-4xl md:text-5xl text-stone-900 mb-5 leading-tight">
            Join us — <span className="italic text-amber-800">all are welcome.</span>
          </h2>
          <p className="text-stone-600 text-lg leading-relaxed mb-10">
            All events are free and open to the public. Come as you are — beginner or seasoned
            practitioner.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="inline-flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-600 text-black px-8 py-4 rounded-full text-sm tracking-wide"
            >
              Get Event Notifications <ArrowRight size={16} />
            </a>
            <a
              href="/dana-schedule"
              className="inline-flex items-center justify-center gap-2 border border-stone-300 hover:border-amber-300 text-stone-900 hover:text-amber-800 px-8 py-4 rounded-full text-sm tracking-wide"
            >
              View Dāna Schedule
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Events;
